"use client"

import { create } from "zustand"
import { toast } from "sonner"
import type { VisitorQuotesState, VisitorQuotesActions } from "./types"
import {
  createIndividualQuoteApi,
  createCorporateQuoteApi,
  getVisitorQuoteByIdApi,
  getVisitorQuotesByIdApi,
  fetchCategoriesApi,
  deleteVisitorQuoteApi,
  getTierOptionsApi,
  updateQuoteSelectionsApi,
  patchQuoteStatusApi,
  resetVisitorQuotesApi,
  fetchContactedSummaryApi,
  mergeContactedApi,
  updateVisitorPricingOverridesApi,
} from "./api"
import { setCookie, getCookie, deleteCookie } from "@/utils/cookies"

export const COOKIE_NAME = "nova_visitor_quote_id"

const initialState: VisitorQuotesState = {
  quoteRequest: null,
  generatedQuotes: [],
  categories: [],
  isLoading: false,
  error: null,
  listLoading: false,
  listError: null,
  selectedTpa: null,
  usaCanadaIncluded: null,
  beneficiaryGroup: null,
  contactedSummary: null,
  summaryLoading: false,
  contactingCategory: null,
  isResetting: false,
  mergeResult: null,
  isMerging: false,
}

export const useVisitorQuotesStore = create<
  VisitorQuotesState & VisitorQuotesActions
>()((set, get) => ({
  ...initialState,

  createIndividual: async (payload) => {
    set({ isLoading: true, error: null })
    try {
      const data = await createIndividualQuoteApi(payload)
      setCookie(COOKIE_NAME, data.visitorId)
      set({ quoteRequest: data, isLoading: false })
      // toast.success("Individual quote request created successfully")
      return true
    } catch (err: unknown) {
      const apiError = err as { error?: { message?: string } }
      const message =
        apiError?.error?.message ?? "Failed to create individual quote request"
      set({ isLoading: false, error: message })
      toast.error(message)
      return false
    }
  },

  createCorporate: async (payload) => {
    set({ isLoading: true, error: null })
    try {
      const data = await createCorporateQuoteApi(payload)
      setCookie(COOKIE_NAME, data?.visitorId)
      set({ quoteRequest: data, isLoading: false })
      return true
    } catch (err: unknown) {
      const apiError = err as { error?: { message?: string } }
      const message =
        apiError?.error?.message ?? "Failed to create corporate quote request"
      set({ isLoading: false, error: message })
      toast.error(message)
      return false
    }
  },

  fetchById: async (id) => {
    set({ isLoading: true, error: null })
    try {
      const data = await getVisitorQuoteByIdApi(id)
      set({ quoteRequest: data, isLoading: false })
    } catch (err: unknown) {
      const apiError = err as { error?: { message?: string } }
      const message =
        apiError?.error?.message ?? "Failed to fetch quote request"
      set({ isLoading: false, error: message })
    }
  },

  fetchByIdFromCookie: async () => {
    const id = getCookie(COOKIE_NAME)
    if (!id) return
    const { quoteRequest, isLoading, fetchById } = get()
    if (isLoading) return
    if (quoteRequest?.id === id) return
    await fetchById(id)
  },

  fetchGeneratedQuotes: async (id: string, benefitCategory?: string) => {
    set({ listLoading: true, listError: null })
    try {
      const res = await getVisitorQuotesByIdApi(id, benefitCategory)
      const quotes = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : []
      set({
        generatedQuotes: quotes,
        listLoading: false,
      })
    } catch (err: unknown) {
      const apiError = err as { error?: { message?: string } }
      const message =
        apiError?.error?.message ?? "Failed to fetch generated quotes"
      set({ listLoading: false, listError: message })
      toast.error(message)
    }
  },

  fetchGeneratedQuotesFromCookie: async (benefitCategory?: string) => {
    const id = getCookie(COOKIE_NAME)
    if (!id) return
    const { fetchGeneratedQuotes } = get()
    await fetchGeneratedQuotes(id, benefitCategory)
  },

  fetchCategories: async (id: string) => {
    try {
      const res = await fetchCategoriesApi(id)
      const cats = Array.isArray(res?.categories) ? res.categories : []
      set({ categories: cats })
    } catch {
      // Categories are optional — non-SME visitors won't have them
      set({ categories: [] })
    }
  },

  fetchCategoriesFromCookie: async () => {
    const id = getCookie(COOKIE_NAME)
    if (!id) return
    const { fetchCategories, fetchById, quoteRequest } = get()
    // Ensure quote metadata is loaded so we can check request_type
    let quote = quoteRequest
    if (!quote || quote.id !== id) {
      await fetchById(id)
      quote = get().quoteRequest
    }
    // Categories endpoint only exists for corporate (SME) quotes
    if (quote?.request_type === "individual") {
      set({ categories: ["individual"] })
      return
    }
    await fetchCategories(id)
  },

  deleteQuote: async (id) => {
    set({ error: null })
    try {
      await deleteVisitorQuoteApi(id)
      deleteCookie(COOKIE_NAME)
      set({ quoteRequest: null })
      toast.success("Quote request deleted successfully")
      return true
    } catch (err: unknown) {
      const apiError = err as { error?: { message?: string } }
      const message =
        apiError?.error?.message ?? "Failed to delete quote request"
      set({ error: message })
      toast.error(message)
      return false
    }
  },

  editIndividual: async (payload) => {
    const existingId = getCookie(COOKIE_NAME)
    if (existingId) {
      try {
        // await deleteVisitorQuoteApi(existingId)
        // deleteCookie(COOKIE_NAME)
      } catch {
        // If delete fails (already deleted), continue with create
      }
    }
    set({ quoteRequest: null, categories: [], generatedQuotes: [] })
    const { createIndividual } = get()
    return createIndividual(payload)
  },

  editCorporate: async (payload) => {
    const existingId = getCookie(COOKIE_NAME)
    if (existingId) {
      try {
        // await deleteVisitorQuoteApi(existingId)
        // deleteCookie(COOKIE_NAME)
      } catch {
        // If delete fails (already deleted), continue with create
      }
    }
    set({ quoteRequest: null, categories: [], generatedQuotes: [] })
    const { createCorporate } = get()
    return createCorporate(payload)
  },

  clearQuote: () => {
    deleteCookie(COOKIE_NAME)
    set({ quoteRequest: null })
  },

  reset: () => {
    deleteCookie(COOKIE_NAME)
    set(initialState)
  },

  getTierOptions: async (productCode, tierId, visaEmirate) => {
    try {
      return await getTierOptionsApi(productCode, tierId, visaEmirate)
    } catch {
      return null
    }
  },

  contactQuote: async (quoteId, benefitCategory?) => {
    set({ contactingCategory: benefitCategory })
    try {
      const res = await patchQuoteStatusApi(quoteId, {
        status: "contacted",
        ...(benefitCategory ? { benefit_category: benefitCategory } : {}),
      })
      // Build contactedSummary directly from the PATCH response — no extra GET needed
      const contacted = res.contactedQuotes ?? []
      const allCats = get().categories
      const categoriesSelected = contacted.map((q) => q.benefitCategory)
      const isComplete =
        allCats.length > 0 &&
        allCats.every((c) => categoriesSelected.includes(c))
      const groups = contacted.map((q) => ({
        benefit_category: q.benefitCategory,
        quoteId: q.id,
        planName: q.planTier.name,
        planTierName: q.planTier.name,
        productName: q.product.name,
        totalWithVat: q.totalWithVat,
        currency: q.currency,
        memberCount: q.memberCount,
        planTier: q.planTier,
      }))
      const totalWithVat = contacted.reduce((s, q) => s + q.totalWithVat, 0)
      set({
        contactingCategory: null,
        contactedSummary: {
          success: true,
          visitorId: res.visitor?.visitorId ?? "",
          requestType: res.visitor?.request_type ?? "corporate",
          currency: contacted[0]?.currency ?? "AED",
          contactedCount: contacted.length,
          categoriesSelected,
          allCategories: allCats,
          isComplete,
          groups,
          totalWithVat,
          pdfKey: contacted[0].pdfKey,
        },
      })
      return true
    } catch (err: unknown) {
      const apiError = err as { error?: { message?: string } }
      const message = apiError?.error?.message ?? "Failed to contact quote"
      toast.error(message)
      set({ contactingCategory: null })
      return false
    }
  },

  resetQuotes: async () => {
    const id = getCookie(COOKIE_NAME)
    if (!id) return false
    set({ isResetting: true })
    try {
      await resetVisitorQuotesApi(id)
      // Refetch categories and quotes after reset
      const { fetchCategories, fetchGeneratedQuotes } = get()
      await fetchCategories(id)
      const cats = get().categories
      // Fetch quotes: if categories exist, fetch for first category; otherwise fetch all quotes
      if (cats.length > 0) {
        await fetchGeneratedQuotes(id, cats[0])
      } else {
        await fetchGeneratedQuotes(id)
      }
      set({
        isResetting: false,
        contactedSummary: null,
        selectedTpa: null,
        usaCanadaIncluded: null,
        beneficiaryGroup: null,
        mergeResult: null,
      })
      return true
    } catch (err: unknown) {
      const apiError = err as { error?: { message?: string } }
      const message = apiError?.error?.message ?? "Failed to reset quotes"
      toast.error(message)
      set({ isResetting: false })
      return false
    }
  },

  fetchContactedSummary: async () => {
    const id = getCookie(COOKIE_NAME)
    if (!id) return
    set({ summaryLoading: true })
    try {
      const raw = await fetchContactedSummaryApi(id)
      // Normalize: the GET response may return groups directly or via contactedQuotes
      // We re-map to ensure planTier.id is always present for highlight matching
      const rawAny = raw as unknown as Record<string, unknown>
      const contacted = (rawAny.contactedQuotes ?? raw.groups ?? []) as Array<{
        planTierName: string
        planTierId?: string
        id?: string
        quoteId?: string
        benefitCategory?: string
        benefit_category?: string
        planName?: string
        productName?: string
        totalWithVat: number
        currency: string
        memberCount: number
        product?: { name: string }
        planTier?: {
          id: string
          name: string
          annualLimit: string
          roomType: string
          networkIp: string
        }
      }>
      const allCats = get().categories
      const groups = contacted.map((q) => ({
        benefit_category: q.benefitCategory ?? q.benefit_category ?? "",
        quoteId: q.id ?? q.quoteId ?? "",
        planTierId: q.planTierId ?? q.planTier?.id,
        planName: q.planTier?.name ?? q.planName ?? "",
        productName: q.product?.name ?? q.productName ?? "",
        totalWithVat: q.totalWithVat,
        currency: q.currency,
        memberCount: q.memberCount,
        planTier: q.planTier,
        planTierName: q.planTierName,
      }))
      const categoriesSelected = groups
        .map((g) => g.benefit_category)
        .filter(Boolean)
      const isComplete =
        raw.isComplete ??
        (allCats.length > 0 &&
          allCats.every((c) => categoriesSelected.includes(c)))
      const totalWithVat =
        raw.totalWithVat ?? groups.reduce((s, g) => s + g.totalWithVat, 0)
      set({
        contactedSummary: {
          success: true,
          visitorId: raw.visitorId ?? id,
          requestType: raw.requestType ?? "corporate",
          currency: raw.currency ?? groups[0]?.currency ?? "AED",
          contactedCount: raw.contactedCount ?? groups.length,
          categoriesSelected,
          allCategories: raw.allCategories ?? allCats,
          isComplete,
          groups,
          totalWithVat,
          totalNet: raw.totalNet,
          totalGross: raw.totalGross,
          novaLoadingPct: raw.novaLoadingPct,
          brokerLoadingPct: raw.brokerLoadingPct,
          novaLoadingOverride:
            raw.total?.novaLoadingOverride ?? raw.novaLoadingOverride,
          brokerLoadingOverride:
            raw.total?.brokerLoadingOverride ?? raw.brokerLoadingOverride,
          discountPercentage:
            raw.total?.discountPercentage ?? raw.discountPercentage,
          pdfKey: raw.pdfKey,
        },
        summaryLoading: false,
      })
    } catch (err: unknown) {
      const apiError = err as { error?: { message?: string } }
      const message = apiError?.error?.message ?? "Failed to fetch summary"
      set({ summaryLoading: false, error: message })
    }
  },

  updateQuoteSelections: async (quoteId, payload) => {
    try {
      await updateQuoteSelectionsApi(quoteId, payload)
      return true
    } catch (err: unknown) {
      const apiError = err as { error?: { message?: string } }
      const message =
        apiError?.error?.message ?? "Failed to update quote selections"
      toast.error(message)
      return false
    }
  },

  mergeContacted: async () => {
    const id = getCookie(COOKIE_NAME)
    if (!id) return null
    set({ isMerging: true })
    try {
      const result = await mergeContactedApi(id)
      set({ mergeResult: result, isMerging: false })
      return result
    } catch (err: unknown) {
      const apiError = err as { error?: { message?: string } }
      const message =
        apiError?.error?.message ?? "Failed to generate merged quote"
      toast.error(message)
      set({ isMerging: false })
      return null
    }
  },

  updateVisitorPricingOverrides: async (payload) => {
    const id = getCookie(COOKIE_NAME)
    if (!id) throw new Error("No visitor session found")
    try {
      const result = await updateVisitorPricingOverridesApi(id, payload)
      // Refresh contacted summary to update the state with new pricing
      const { fetchContactedSummary, fetchGeneratedQuotes, quoteRequest } = get()
      await fetchContactedSummary()
      
      // Refresh quotes to show updated loadingBreakdown in plan cards
      const summary = get().contactedSummary
      if (summary?.categoriesSelected && summary.categoriesSelected.length > 0) {
        // SME: Refresh all contacted categories
        for (const category of summary.categoriesSelected) {
          await fetchGeneratedQuotes(id, category)
        }
      } else {
        // Individual: Refresh all quotes (no category filter)
        await fetchGeneratedQuotes(id)
      }
      
      return result
    } catch (err) {
      throw err
    }
  },
}))

export type {
  ContactedSummaryResponse,
  ContactedSummaryGroup,
  VisitorQuoteRequest,
  IndividualDetail,
  VisitorMember,
  CorporateDetail,
  CensusMember,
  QuoteContact,
  CreateIndividualQuotePayload,
  TierOptionsResponse,
  TierAdjustmentFactor,
  TierAdjustmentFactorOption,
  TierOptionalBenefit,
  TpaEntry,
  TpaConfig,
  UpdateQuoteSelectionsPayload,
  CreateCorporateQuotePayload,
  VisitorQuotesState,
  VisitorQuotesActions,
  GeneratedQuote,
  MergeContactedResponse,
  MergeContactedGroup,
} from "./types"
